import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '../components/Navigation';
import WhatsAppFloat from '../components/WhatsAppFloat';
import ImageLightbox from '../components/ImageLightbox';
import SEO from '../components/SEO';
import axios from 'axios';
import { ImageGallerySchema, BreadcrumbSchema } from '../utils/schemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProyectosPage() {
  const [projects, setProjects] = useState([]);
  const [selectedClassification, setSelectedClassification] = useState('all');
  const [classifications, setClassifications] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchProjects();
    fetchClassifications();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchClassifications = async () => {
    try {
      const response = await axios.get(`${API}/classifications`);
      setClassifications(['all', ...response.data.classifications]);
    } catch (error) {
      console.error('Error fetching classifications:', error);
    }
  };

  const filteredProjects = (selectedClassification === 'all'
    ? projects
    : projects.filter(p => p.classification === selectedClassification)
  ).filter(p => (p.status || 'ready') === 'ready' && p.photos && p.photos.length > 0);

  const openLightbox = (photos, index) => {
    setLightboxImages(photos);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Proyectos — EMABRAW | Fotografía Documental"
        description="Explora proyectos de fotografía documental y artística de Edgardo Andrade. Series fotográficas, street photography y trabajo autoral desde Panamá."
        image="/og-proyectos.jpg"
        type="website"
        keywords="proyectos fotográficos, galería fotografía, Edgardo Andrade proyectos, fotografía documental Panamá, exposiciones fotografía"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            ImageGallerySchema(projects),
            BreadcrumbSchema([
              { name: "Inicio", url: "https://www.emabraw.com" },
              { name: "Proyectos", url: "https://www.emabraw.com/proyectos" }
            ])
          ]
        }}
      />

      <Navigation />
      <WhatsAppFloat />

      {/* Header */}
      <section className="px-6 sm:px-12 lg:px-24 pt-32 pb-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-neutral-500">Portafolio</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white">
              Proyectos
            </h1>
            <p className="text-base leading-relaxed text-neutral-400 max-w-2xl">
              Fotografía documental, series artísticas y trabajo autoral
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="px-6 sm:px-12 lg:px-24 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3" data-testid="classification-filter">
            {classifications.map((classification) => (
              <button
                key={classification}
                onClick={() => setSelectedClassification(classification)}
                className={`px-6 py-2 text-sm transition-colors ${
                  selectedClassification === classification
                    ? 'bg-white text-black'
                    : 'bg-neutral-900 text-neutral-400 hover:text-white border border-neutral-800'
                }`}
                data-testid={`filter-${classification}`}
              >
                {classification === 'all' ? 'Todos' : classification}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="px-6 sm:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto space-y-16">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.project_id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="space-y-6"
              data-testid={`project-item-${index}`}
            >
              {/* Project Info */}
              <div className="space-y-2">
                <p className="text-xs tracking-[0.2em] uppercase text-neutral-500">
                  {project.classification}
                </p>
                <h3 className="text-2xl md:text-3xl text-white font-medium">{project.title}</h3>
                <p className="text-sm text-neutral-400">{project.date}</p>
                <p className="text-sm text-neutral-400 max-w-3xl">{project.description}</p>
              </div>

              {/* Horizontal Carousel */}
              <div className="relative">
                <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-neutral-900">
                  <div className="flex gap-4" style={{ width: 'max-content' }}>
                    {project.photos.map((photo, photoIndex) => (
                      <motion.button
                        key={photoIndex}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => openLightbox(project.photos, photoIndex)}
                        className="relative flex-shrink-0 overflow-hidden group cursor-pointer"
                        style={{ width: '400px', height: '500px' }}
                        data-testid={`project-photo-${photoIndex}`}
                      >
                        <picture>
                          <source 
                            media="(max-width: 768px)" 
                            srcSet={photo.mobile_url || photo.url}
                          />
                          <img
                            src={photo.url}
                            alt={photo.name || `${project.title} ${photoIndex + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </picture>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm">
                            Click para ampliar
                          </span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
                
                {project.photos.length > 1 && (
                  <p className="text-xs text-neutral-500 mt-2">
                    ← Desliza para ver más ({project.photos.length} fotos)
                  </p>
                )}
              </div>
            </motion.div>
          ))}

          {filteredProjects.length === 0 && (
            <div className="text-center py-24">
              <p className="text-neutral-400">No hay proyectos disponibles</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <ImageLightbox
            images={lightboxImages}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default ProyectosPage;